/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, HFlow, Text, Theme, useTheme } from 'bold-ui'
import { ColorScale } from 'bold-ui/lib/styles/colors'
import { cloneElement, ReactElement, useMemo } from 'react'

interface TipoCompartilhamentoButtonProps {
  text: string
  color: ColorScale
  backgroundColor: string
  colorHoverAtivo: string
  icon: ReactElement
  size: 'small' | 'large'
  isAtivo: boolean
  disabled?: boolean
  onClick: () => void
}

export function TipoCompartilhamentoButton(props: Readonly<TipoCompartilhamentoButtonProps>) {
  const { text, color, backgroundColor, colorHoverAtivo, icon, size, isAtivo, disabled, onClick } = props

  const theme = useTheme()
  const classes = useMemo(() => getStyles(theme, color, isAtivo, backgroundColor, colorHoverAtivo, size, disabled), [
    theme,
    color,
    isAtivo,
    backgroundColor,
    colorHoverAtivo,
    size,
    disabled,
  ])

  const iconSize = size === 'small' ? '1.5rem' : '2.25rem'

  return (
    <Button size={size} style={classes.button} onClick={onClick} disabled={disabled}>
      <HFlow hSpacing={0.5} alignItems='center'>
        <span css={classes.icon}>{cloneElement(icon, { width: iconSize, height: iconSize })}</span>
        <Text fontWeight='bold' style={classes.text}>
          {text}
        </Text>
      </HFlow>
    </Button>
  )
}

const getStyles = (
  theme: Theme,
  color: ColorScale,
  isAtivo: boolean,
  backgroundColor: string,
  colorHoverAtivo: string,
  size: 'small' | 'large',
  disabled?: boolean
) => {
  const borderColor = disabled ? color.c80 : color.c40
  const border = isAtivo ? '1px solid transparent' : `1px solid ${borderColor}`
  const backgroundColorFinal = isAtivo ? backgroundColor : theme.pallete.gray.c100
  const hoverBackground = isAtivo ? colorHoverAtivo : color.c90
  const textColorWhenEnabled = isAtivo ? theme.pallete.gray.c100 : color.c40
  const textColor = disabled ? color.c80 : textColorWhenEnabled
  const width = size === 'small' ? '12.75rem' : '13.5rem'
  const height = size === 'small' ? '2.5rem' : '3.25rem'

  return {
    button: css`
      width: ${width};
      height: ${height};
      padding: 0;
      min-inline-size: max-content;
      align-content: center;
      border: ${border};
      border-radius: 2px;
      background-color: ${backgroundColorFinal};
      &:not(:disabled):hover {
        background-color: ${hoverBackground};
      }
      &:focus-visible {
        outline: none;
      }
    `,
    text: css`
      color: ${textColor};
    `,
    icon: css`
      display: flex;
      align-items: center;
    `,
  }
}
